import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, X, Filter } from 'lucide-react';

interface TherapyFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedStage: string;
  onStageChange: (stage: string) => void;
  selectedEvidenceLevel: string;
  onEvidenceLevelChange: (level: string) => void;
  selectedStreamType: string;
  onStreamTypeChange: (type: string) => void;
  evidenceLevels: string[];
  streamTypes: string[];
  onClear: () => void;
  hasActiveFilters: boolean;
}

const TherapyFilters: React.FC<TherapyFiltersProps> = ({
  searchQuery,
  onSearchChange,
  selectedStage,
  onStageChange,
  selectedEvidenceLevel,
  onEvidenceLevelChange,
  selectedStreamType,
  onStreamTypeChange,
  evidenceLevels,
  streamTypes,
  onClear,
  hasActiveFilters,
}) => {
  const { t } = useTranslation('therapyLibrary');

  return (
    <div className="bg-white rounded-lg border border-amber-100 p-4 shadow-sm space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-earth-400" />
        <Input
          type="text"
          placeholder={t('searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 border-amber-200 focus:border-amber-400"
        />
      </div>

      {/* Filter Row */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-stretch sm:items-center">
        <Filter className="h-4 w-4 text-earth-500 hidden sm:block flex-shrink-0" />
        
        {/* Stage Filter */}
        <Select value={selectedStage} onValueChange={onStageChange}>
          <SelectTrigger className="w-full sm:w-[140px] md:w-[160px] border-amber-200">
            <SelectValue placeholder={t('filters.stage')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allStages')}</SelectItem>
            <SelectItem value="0">Stage 0: {t('stages.0.title')}</SelectItem>
            <SelectItem value="1">Stage 1: {t('stages.1.title')}</SelectItem>
            <SelectItem value="2">Stage 2: {t('stages.2.title')}</SelectItem>
            <SelectItem value="3">Stage 3: {t('stages.3.title')}</SelectItem>
            <SelectItem value="4">Stage 4: {t('stages.4.title')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Evidence Level Filter */}
        <Select value={selectedEvidenceLevel} onValueChange={onEvidenceLevelChange}>
          <SelectTrigger className="w-full sm:w-[140px] md:w-[160px] border-amber-200">
            <SelectValue placeholder={t('filters.evidenceLevel')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allLevels')}</SelectItem>
            {evidenceLevels.map(level => (
              <SelectItem key={level} value={level}>
                {t(`evidenceLevels.${level}`)} ({level})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Stream Type Filter */}
        <Select value={selectedStreamType} onValueChange={onStreamTypeChange}>
          <SelectTrigger className="w-full sm:w-[140px] md:w-[160px] border-amber-200">
            <SelectValue placeholder={t('filters.streamType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allTypes')}</SelectItem>
            {streamTypes.map(type => (
              <SelectItem key={type} value={type}>
                {t(`streamTypes.${type}`, type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-amber-700 hover:text-amber-900 hover:bg-amber-50 w-full sm:w-auto"
          >
            <X className="h-4 w-4 mr-1" />
            {t('filters.clear')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default TherapyFilters;
